import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
    CreditCard, DollarSign, Loader2, Building2, Check, ArrowRight,
    Receipt, Clock, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { BillingMessageEditor } from '../../components/billing/ManualPaymentsCard';

const plans = {
    basic: { name: 'Basic', monthly: 299, yearly: 2990, color: 'text-sky-400 bg-sky-500/10' },
    pro: { name: 'Professional', monthly: 799, yearly: 7990, color: 'text-amber-400 bg-amber-500/10' },
    enterprise: { name: 'Enterprise', monthly: 1999, yearly: 19990, color: 'text-violet-400 bg-violet-500/10' },
};

const statusIcons = {
    initiated: <Clock className="w-4 h-4 text-amber-500" />,
    pending: <Clock className="w-4 h-4 text-amber-500" />,
    paid: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    unpaid: <XCircle className="w-4 h-4 text-red-500" />,
    expired: <AlertCircle className="w-4 h-4 text-muted-foreground" />,
};

const statusColors = {
    initiated: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    paid: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    unpaid: 'bg-red-500/10 text-red-500 border-red-500/20',
    expired: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const usd = (n) => `USD ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

function ManualBillingSummary({ overview }) {
    const rows = overview.tenants.filter(t => t.billable);
    return (
        <Card className="card-aviation" data-testid="manual-billing-summary">
            <CardHeader className="pb-3">
                <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground">Cobro mensual esperado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-6 text-sm">
                    <div>A cobrar: <span className="font-mono font-bold" data-testid="summary-to-collect">{usd(overview.totals.to_collect_monthly)}</span></div>
                    <div>En piloto (lista): <span className="font-mono font-bold" data-testid="summary-in-pilot">{usd(overview.totals.in_pilot_list_monthly)}</span></div>
                </div>
                <div className="space-y-1">
                    {rows.map(t => (
                        <div key={t.id} className="flex justify-between gap-3 text-sm py-1 border-b border-border/30" data-testid={`summary-row-${t.id}`}>
                            <span>{t.name} <span className="text-xs text-muted-foreground">· {t.plan_name} · {t.aircraft_count} aeronaves facturables{t.pilot_active ? ' · piloto' : ''}</span></span>
                            <span className="font-mono">{usd(t.amount_due_monthly)}</span>
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-muted-foreground">Aeronaves facturables: incluye todas las aeronaves salvo las almacenadas.</p>
            </CardContent>
        </Card>
    );
}

export default function AdminBillingPage() {
    const { apiCall, admin } = useAdminAuth();
    const [searchParams] = useSearchParams();
    const [transactions, setTransactions] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCheckout, setShowCheckout] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState('pro');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [processing, setProcessing] = useState(false);
    const [polling, setPolling] = useState(false);
    const [overview, setOverview] = useState(null);
    const stripeEnabled = !!overview?.stripe_enabled;

    const fetchData = useCallback(async () => {
        try {
            const [txnRes, tenantRes] = await Promise.all([
                apiCall('get', '/admin/billing/transactions'),
                apiCall('get', '/admin/tenants')
            ]);
            setTransactions(txnRes.data);
            setTenants(tenantRes.data);
            apiCall('get', '/admin/billing/overview').then(r => setOverview(r.data)).catch(() => {});
        } catch (e) {
            console.error(e);
            toast.error('No se pudo cargar la facturación');
        } finally {
            setLoading(false);
        }
    }, [apiCall]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auditoria 8 - Item 7: eliminado el polling legacy de /admin/billing/status.
    // El flujo de checkout ahora usa subscriptions (/admin/billing/subscription/*).
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            setPolling(true);
            let attempts = 0;
            const poll = async () => {
                if (attempts >= 5) {
                    setPolling(false);
                    toast.info('Verificación de suscripción en proceso. Actualiza la página en unos momentos.');
                    return;
                }
                try {
                    const res = await apiCall('get', `/admin/billing/subscription/status/${sessionId}`);
                    if (res.data.payment_status === 'paid') {
                        toast.success('Suscripción activada.');
                        setPolling(false);
                        fetchData();
                        window.history.replaceState({}, '', '/admin/billing');
                        return;
                    } else if (res.data.status === 'expired') {
                        toast.error('Sesión de pago expirada.');
                        setPolling(false);
                        window.history.replaceState({}, '', '/admin/billing');
                        return;
                    }
                } catch (e) {
                    console.error(e);
                    toast.error('No se pudo cargar la facturación');
                }
                attempts++;
                setTimeout(poll, 2000);
            };
            poll();
        }
    }, [searchParams, apiCall, fetchData]);

    const handleCheckout = async () => {
        if (!showCheckout) return;
        setProcessing(true);
        try {
            // Auditoria 8 - Item 7: usa subscription checkout (recurring billing) en vez del legacy one-shot
            const res = await apiCall('post', '/admin/billing/subscription/checkout', {
                tenant_id: showCheckout.id,
                plan: selectedPlan,
                billing_cycle: billingCycle,
                origin_url: window.location.origin
            });
            // Redirect to Stripe
            window.location.href = res.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear sesión de suscripción');
            setProcessing(false);
        }
    };

    const getTenantName = (id) => tenants.find(t => t.id === id)?.name || id.slice(0, 8) + '...';

    const totalRevenue = transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + t.amount, 0);
    const paidCount = transactions.filter(t => t.payment_status === 'paid').length;
    const pendingCount = transactions.filter(t => t.payment_status === 'initiated' || t.payment_status === 'pending').length;

    return (
        <div className="space-y-6">
            {/* Polling indicator */}
            {polling && (
                <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-500 text-sm animate-pulse-glow">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verificando pago...
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="admin-billing-title">
                        Facturación
                    </h1>
                    <p className="text-muted-foreground mt-1">{stripeEnabled ? 'Gestión de pagos y suscripciones' : 'Cobro por aeronave (facturación manual)'}</p>
                </div>
            </div>

            {!stripeEnabled && admin?.role === 'superadmin' && (
                <Card className="card-aviation"><CardContent className="p-4 space-y-2">
                    <BillingMessageEditor apiCall={apiCall} />
                    <p className="text-xs text-muted-foreground">Los pagos de cada aerolínea se registran en su ficha (Empresas → ficha → Pagos manuales).</p>
                </CardContent></Card>
            )}

            {/* KPIs de Stripe (ocultos en facturación manual) */}
            {stripeEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                            <DollarSign className="w-6 h-6 text-emerald-500" />
                        </div>
                        <div>
                            <p className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Ingresos Totales</p>
                            <p className="text-2xl font-bold font-mono" data-testid="billing-total-revenue">${totalRevenue.toLocaleString()}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-6 h-6 text-sky-500" />
                        </div>
                        <div>
                            <p className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Pagos Exitosos</p>
                            <p className="text-2xl font-bold font-mono">{paidCount}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card-aviation">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                            <p className="text-xs font-bold tracking-widest text-muted-foreground uppercase">Pagos Pendientes</p>
                            <p className="text-2xl font-bold font-mono">{pendingCount}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
            )}

            {overview && <ManualBillingSummary overview={overview} />}

            {/* Tenants needing billing (Stripe; oculto mientras STRIPE_ENABLED=false) */}
            {stripeEnabled && (
            <Card className="card-aviation">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground">
                        Aerolíneas - Activar Suscripción
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {tenants.filter(t => t.plan === 'free' || t.status === 'trial').map(t => (
                            <div key={t.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/20 transition-colors">
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-5 h-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm font-medium">{t.name}</p>
                                        <p className="text-xs text-muted-foreground font-mono">{t.icao_code} - Plan: {t.plan}</p>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => { setShowCheckout(t); setSelectedPlan('pro'); }}
                                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs"
                                    data-testid={`upgrade-tenant-${t.id}`}
                                >
                                    <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Upgrade
                                </Button>
                            </div>
                        ))}
                        {tenants.filter(t => t.plan === 'free' || t.status === 'trial').length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">Todas las aerolíneas tienen plan activo</p>
                        )}
                    </div>
                </CardContent>
            </Card>
            )}

            {/* Transaction History (Stripe; oculto en facturación manual) */}
            {stripeEnabled && (
            <Card className="card-aviation overflow-hidden">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                        <Receipt className="w-4 h-4" /> Historial de Transacciones
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table-aviation" data-testid="billing-table">
                                <thead>
                                    <tr>
                                        <th>Aerolínea</th>
                                        <th>Plan</th>
                                        <th>Ciclo</th>
                                        <th>Monto</th>
                                        <th>Estado</th>
                                        <th>Fecha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((txn) => (
                                        <tr key={txn.id} data-testid={`txn-row-${txn.id}`}>
                                            <td className="font-medium">{getTenantName(txn.tenant_id)}</td>
                                            <td><Badge className={plans[txn.plan]?.color || ''}>{plans[txn.plan]?.name || txn.plan}</Badge></td>
                                            <td className="text-sm">{txn.billing_cycle === 'yearly' ? 'Anual' : 'Mensual'}</td>
                                            <td className="font-mono font-bold">${txn.amount?.toLocaleString()}</td>
                                            <td>
                                                <Badge className={statusColors[txn.payment_status] || ''} variant="outline">
                                                    <span className="flex items-center gap-1.5">
                                                        {statusIcons[txn.payment_status]}
                                                        {txn.payment_status}
                                                    </span>
                                                </Badge>
                                            </td>
                                            <td className="text-xs text-muted-foreground">{new Date(txn.created_at).toLocaleString('es-ES')}</td>
                                        </tr>
                                    ))}
                                    {transactions.length === 0 && (
                                        <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">
                                            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                            <p>Sin transacciones</p>
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Checkout Dialog */}
            <Dialog open={stripeEnabled && !!showCheckout} onOpenChange={() => setShowCheckout(null)}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight flex items-center gap-2">
                            <CreditCard className="w-6 h-6 text-amber-500" />
                            Activar Suscripción
                        </DialogTitle>
                    </DialogHeader>
                    {showCheckout && (
                        <div className="space-y-5">
                            <div className="p-3 rounded-lg bg-muted/30">
                                <p className="text-xs text-muted-foreground">Aerolínea</p>
                                <p className="font-medium">{showCheckout.name} <span className="text-xs font-mono text-muted-foreground ml-1">({showCheckout.icao_code})</span></p>
                            </div>

                            <div className="space-y-2">
                                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Plan</p>
                                <div className="grid grid-cols-3 gap-2">
                                    {Object.entries(plans).map(([key, plan]) => (
                                        <button
                                            key={key}
                                            onClick={() => setSelectedPlan(key)}
                                            className={`p-3 rounded-lg border text-center transition-all ${selectedPlan === key ? 'border-amber-500 bg-amber-500/10' : 'border-border hover:border-border/80'}`}
                                            data-testid={`checkout-plan-${key}`}
                                        >
                                            <p className="text-xs font-bold">{plan.name}</p>
                                            <p className="text-lg font-mono font-bold mt-1">${billingCycle === 'yearly' ? plan.yearly.toLocaleString() : plan.monthly}</p>
                                            <p className="text-xs text-muted-foreground">/{billingCycle === 'yearly' ? 'año' : 'mes'}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Ciclo de Facturación</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => setBillingCycle('monthly')}
                                        className={`p-2.5 rounded-lg border text-center text-sm transition-all ${billingCycle === 'monthly' ? 'border-amber-500 bg-amber-500/10' : 'border-border'}`}
                                        data-testid="checkout-cycle-monthly"
                                    >
                                        Mensual
                                    </button>
                                    <button
                                        onClick={() => setBillingCycle('yearly')}
                                        className={`p-2.5 rounded-lg border text-center text-sm transition-all ${billingCycle === 'yearly' ? 'border-amber-500 bg-amber-500/10' : 'border-border'}`}
                                        data-testid="checkout-cycle-yearly"
                                    >
                                        Anual <span className="text-xs text-emerald-500 ml-1">-17%</span>
                                    </button>
                                </div>
                            </div>

                            <div className="p-4 rounded-lg bg-muted/30 flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Total a cobrar</span>
                                <span className="text-2xl font-mono font-bold">
                                    ${(billingCycle === 'yearly' ? plans[selectedPlan].yearly : plans[selectedPlan].monthly).toLocaleString()} <span className="text-sm text-muted-foreground font-normal">USD</span>
                                </span>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCheckout(null)}>Cancelar</Button>
                        <Button onClick={handleCheckout} disabled={processing} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs" data-testid="checkout-submit">
                            {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                            Ir a Pagar con Stripe
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
